/**
 * Reveal — subtle "assemble on enter" wrapper.
 *
 * Wrap any section/element with <Reveal> and it eases up + in as it scrolls
 * into view. With `stagger`, direct children animate in one after another so
 * a section visibly *comes together* rather than just appearing. Respects
 * prefers-reduced-motion.
 */

import { type ReactNode } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** delay before this block starts (seconds) */
  delay?: number;
  /** distance travelled on the way in (px) */
  y?: number;
  /** if true, direct children are revealed one-by-one */
  stagger?: boolean;
  /** gap between staggered children (seconds) */
  staggerGap?: number;
  as?: "div" | "section" | "li";
};

const EASE = [0.22, 1, 0.36, 1] as const;

export function Reveal({
  children,
  className,
  delay = 0,
  y = 28,
  stagger = false,
  staggerGap = 0.1,
  as = "div",
}: RevealProps) {
  const reduced = useReducedMotion();
  const MotionTag = motion[as];

  if (reduced) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }

  if (stagger) {
    const container: Variants = {
      hidden: {},
      show: {
        transition: { staggerChildren: staggerGap, delayChildren: delay },
      },
    };
    return (
      <MotionTag
        className={className}
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
      >
        {children}
      </MotionTag>
    );
  }

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </MotionTag>
  );
}

/**
 * RevealItem — a single child inside a <Reveal stagger> block.
 * Picks up the parent's stagger timing automatically.
 */
export function RevealItem({
  children,
  className,
  y = 24,
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  y?: number;
  as?: "div" | "li";
}) {
  const reduced = useReducedMotion();
  const MotionTag = motion[as];

  if (reduced) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }

  const item: Variants = {
    hidden: { opacity: 0, y },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
  };

  return (
    <MotionTag className={className} variants={item}>
      {children}
    </MotionTag>
  );
}

export default Reveal;
